import {
  Collapsible, DateSort, EmptyData, FlexColumn, Table, TextSort, formatNumber, useAsyncFunctionProgress, useDownloadFile, useStyledTheme,
} from '@equifinance/shared';
import { DateFormatStringEnum, formatDateTime, getDiff } from '@equifinance/shared/utils';
import styled, { css } from 'styled-components';
import useDocumentsApi, { EsisInfo } from '../../api/useDocumentsApi';
import { DownloadIcon, DownloadLink, HiddedA } from './ProductComponents';
import { useAccountContext } from '../../contexts';

const ESISDownloadLink = styled(DownloadLink)`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`}`;

const CollapsibleInner = styled(FlexColumn)`${({ theme }) => `
  gap:${theme.padding[4]};
  margin:${theme.padding[4]};
  border-radius:${theme.borderRadius[2]};
  overflow:hidden;
`}`;

const CollapsibleHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.tertiary[5]};

  .mi-chevron-up, .mi-chevron-down {
    color: ${theme.colours.neutral[2]};
  }
`}`;

const CollapsibleTitleCss = css`${({ theme }) => `
  color:${theme.colours.neutral[2]} !important;
  font-weight: ${theme.fontWeight.bold};
`}`;

function Quote(esisInfo: EsisInfo, downloadESIS: (esisInfo: EsisInfo) => Promise<void>) {
  const { inProgress, execute: executeDownloadESIS } = useAsyncFunctionProgress(downloadESIS);
  const { documentName } = esisInfo;

  return (
    <>
      <DownloadIcon disabled={inProgress} className="mi-document-download" />
      <ESISDownloadLink
        disabled={inProgress}
        onClick={() => {
          if (inProgress) return;
          executeDownloadESIS(esisInfo);
        }}
        aria-hidden="true"
      >
        {documentName}
      </ESISDownloadLink>
    </>
  );
}
export default function EsisHistory({
  esisInfos,
}: {
  esisInfos: EsisInfo[]
}) {
  const { id: accountId } = useAccountContext();

  const { getDocument } = useDocumentsApi();

  const {
    downloadFile, downloadFileRef, // downloadFileUrl, downloadFilename,
  } = useDownloadFile();

  const downloadESIS = async (esisInfo: EsisInfo) => {
    const { documentData, fileName } = await getDocument(accountId, esisInfo.documentId);

    downloadFile(fileName, documentData);
  };

  const columns = [
    {
      name: 'Quote',
      sortable: false,
      width: '300px',
      cell: (esisInfo: EsisInfo) => Quote(esisInfo, downloadESIS),
      sortFunction: TextSort<EsisInfo>('documentName'),
    },
    {
      name: 'Loan amount (£)',
      sortable: false,
      selector: (esisInfo: EsisInfo) => (esisInfo?.loanAmount ? `£${formatNumber(esisInfo.loanAmount)}` : ''),
      sortFunction: TextSort<EsisInfo>('loanAmount'),
    },
    {
      name: 'Term (mth)',
      sortable: false,
      selector: (esisInfo: EsisInfo) => (esisInfo?.termInMonths ? `${esisInfo.termInMonths} Months` : ''),
      sortFunction: TextSort<EsisInfo>('termInMonths'),
    },
    {
      name: 'Product type',
      sortable: false,
      selector: (esisInfo: EsisInfo) => esisInfo.productType || '',
      sortFunction: TextSort<EsisInfo>('productType'),
    },
    {
      name: 'Initial rate',
      sortable: false,
      selector: (esisInfo: EsisInfo) => esisInfo?.initialRate || '',
      sortFunction: TextSort<EsisInfo>('initialRate'),
    },
    {
      name: 'Initial pmt (£)',
      sortable: false,
      selector: (esisInfo: EsisInfo) => (esisInfo?.initialPayment ? `£${formatNumber(esisInfo.initialPayment)}` : ''),
      sortFunction: TextSort<EsisInfo>('initialPayment'),
    },
    {
      name: 'Created on',
      sortable: false,
      selector: (esisInfo: EsisInfo) => (esisInfo.generatedTimeStamp ? formatDateTime(esisInfo?.generatedTimeStamp, DateFormatStringEnum.DDMMYYYY) : ''),
      sortFunction: DateSort<EsisInfo>('generatedTimeStamp'),
    },
  ];

  const theme = useStyledTheme();

  return (
    <Collapsible
      id={`${accountId}-esis-history`}
      title="ESIS History"
      headerCss={CollapsibleHeaderCss}
      titleCss={CollapsibleTitleCss}
    >
      <HiddedA ref={downloadFileRef} />
      <CollapsibleInner>
        <Table
          showSearch={false}
          columns={columns}
          data={esisInfos}
          defaultSortFieldId={7}
          defaultSortAsc={false}
          noDataComponent={<EmptyData message="" />}
          conditionalRowStyles={[
            {
              when: (esisInfo) => !!esisInfo.generatedTimeStamp,
              style: (esisInfo) => ({
                background: `${getDiff(esisInfo.expiryDate, new Date(), 's') < 0
                  ? theme.colours.neutral[6]
                  : theme.colours.neutral[9]} !important`,
              }),
            },
          ]}
          styles={{
            table: {
              style: {
                maxHeight: `${theme.esisHistoryHeight} !important`,
                overflow: `${'auto'} !important`,
              },
            },
            headRow: {
              style: {
                border: `${'none'} !important`,
                backgroundColor: `${theme.colours.neutral[8]} !important`,
                color: `${theme.colours.neutral[2]} !important`,
              },
            },
            rows: {
              style: {
                border: `${'none !important'} !important`,
              },
            },
          }}
        />
      </CollapsibleInner>
    </Collapsible>
  );
}
